import React, { useContext, useEffect, useState } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  MenuItem,
  TextField,
  CircularProgress,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import { useParams } from "react-router-dom";
import useUserClaims from "../../../hooks/useUserClaims";
import { useFormik } from "formik";
import CustomTextField from "../component/CustomTextField";
import { useSnackbar } from "../../../contexts/InfoContext";
import { useAuth } from "../../../contexts/AuthContext";
import getInternationalDate from "../../../utils/getDate";
import { SpinnerContext } from "../../../contexts/SpinnerContext";
import getCurrentTime from "../../../utils/getCurrentTime";
import { CardFeeReportFormValidationSchema } from "../validator/CardFeeReportFormValidationSchema";
import createReport from "../../../api/report/createReport";
import capitalizeString from "../../../utils/capitalizeString";
import { getCardsForCardFeeByBranch } from "../../../api/services/Status/get_cards_for_card_fee";

const CardFeeReportForm = () => {
  const params = useParams();
  const [showForm, setShowForm] = useState(false);
  const { openSnackbar } = useSnackbar();
  const { user } = useAuth();
  const theme = useTheme();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const userClaims = useUserClaims(user);

  let active = "";
  let activeTable = "";
  let activeDailySummery = "";
  let worker = [];
  const storedData = localStorage.getItem("userData");
  if (storedData) {
    const userData = JSON.parse(storedData);
    active = userData ? userData.active : "";
    worker = userData ? userData.worker : [];
    activeTable = userData ? userData.activeTable : "";
    activeDailySummery = userData ? userData.activeDailySummery : "";
  }

  let filteredData = worker?.filter((item) => item.role === "DeliveryGuy");
  let transformedData = filteredData?.map((item) => [item.name, item.id]);
  const [countdown, setCountdown] = useState(null);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [completedCardsData, setCompletedCardsData] = useState([]);

  useEffect(() => {
    if (!params.id) return;

    const unsubscribeAssigned = getCardsForCardFeeByBranch(
      params.id,
      (cards) => {
        setCompletedCardsData(cards);
      }
    );

    return () => unsubscribeAssigned && unsubscribeAssigned();
  }, [params.id]);

  const cardsOptions =
    completedCardsData?.map((item) => ({
      name: item.name,
      id: item.id,
      branchId: item.branchId,
      amount: item.amountBirr,
      block: item.blockHouse,
    })) || [];

  const handleCardsChange = (event) => {
    const { value } = event.target;
    const selectedCardIds =
      typeof value === "string" ? value.split(",") : value;

    const selectedCards = cardsOptions.filter((card) =>
      selectedCardIds.includes(card.id)
    );

    const selectedCardNames = selectedCards.map((card) => card.name);
    const totalSelectedCardsCount = selectedCards.length;
    const totalSelectedCardsAmount = selectedCards.reduce(
      (sum, card) => sum + (card.amount || 0),
      0
    );

    formik.setFieldValue("selectedCardIds", selectedCardIds);
    formik.setFieldValue("selectedCardNames", selectedCardNames);
    formik.setFieldValue("numberOfCard", totalSelectedCardsCount);
    formik.setFieldValue("price", totalSelectedCardsAmount);
  };

  useEffect(() => {
    if (!active) {
      openSnackbar(
        `There is not salary table for this branch, please create one first!`,
        "info"
      );
    }
  }, [active, openSnackbar]);

  const formik = useFormik({
    initialValues: {
      deliveryguyName: "",
      deliveryguyId: "",
      numberOfCard: 0,
      price: 0,
      selectedCardIds: [],
      selectedCardNames: [],
    },
    enableReinitialize: false,
    validationSchema: CardFeeReportFormValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const name = capitalizeString(values.deliveryguyName);
        values.deliveryguyName = name;
        const currentTimeString = getCurrentTime();
        const date = getInternationalDate();
        values.time = currentTimeString;
        values.branchId = params.id;
        values.date = date;
        values.active = active;
        values.activeTable = activeTable;
        values.activeDailySummery = activeDailySummery;
        values.returnCardNumber = 0;
        values.reason = "";

        values.selectedCardIdsForReport = values.selectedCardIds;

        if (!values.deliveryguyId || !values.deliveryguyName) {
          handleCloseForm();
          throw {
            response: {
              data: {
                message:
                  "Delivery guy information is not found. Please check your connection, refresh your browser, and try again.",
                type: "error",
              },
            },
          };
        }

        if (!values.branchId) {
          handleCloseForm();
          throw {
            response: {
              data: {
                message:
                  "Branch information is not found. Please check your connection, refresh your browser, and try again.",
              },
            },
          };
        }

        if (
          !values.active ||
          !values.activeTable ||
          !values.activeDailySummery
        ) {
          handleCloseForm();
          throw {
            response: {
              data: {
                message:
                  "You do not have Salary Table or Daily Table. Create salary table and daily table before. If this is not the issue, please review your internet connection, refresh your web browser, and attempt the action once more.",
                type: "info",
              },
            },
          };
        }

        const res = await createReport(user, values, "CardFee");
        openSnackbar(`${res.data.message} successfully created!`, "success");
        handleCloseForm();
        setTimeout(() => {
          setIsFormSubmitted(false);
          setIsSubmitting(false);
        }, 1000);
      } catch (error) {
        if (error.response && error.response.data) {
          openSnackbar(
            error.response.data.message,
            error.response.data.type ? error.response.data.type : "error"
          );
        } else {
          openSnackbar("An unexpected error occurred.", "error");
        }
        setIsSubmitting(false);
      }
    },
  });

  const handleButtonClick = () => {
    if (active && activeTable) {
      setIsFormSubmitted(false);
      if (!isCountdownActive && !isFormSubmitted) {
        setShowForm(true);
      }
    } else {
      if (!active) {
        openSnackbar(
          `There is not salary table for this branch, please create one first!`,
          "info"
        );
      }
      if (!activeTable) {
        openSnackbar(
          `You do not have table for this branch, please create one first!`,
          "info"
        );
      }
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    formik.resetForm();
  };

  const startCountdown = () => {
    if (!isCountdownActive && !isFormSubmitted) {
      setIsCountdownActive(true);
      setCountdown(5);
    }
  };

  const handleUndo = () => {
    setIsCountdownActive(false);
    setCountdown(null);
  };

  const handleDeliveryGuyChange = (event) => {
    const Id = event.target.value;
    const Name =
      transformedData.find((employee) => employee[1] === Id)?.[0] || "";
    formik.setFieldValue("deliveryguyName", Name);
    formik.setFieldValue("deliveryguyId", Id);
  };

  useEffect(() => {
    let timer;
    if (isCountdownActive && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && isCountdownActive) {
      setIsCountdownActive(false);
      if (!isFormSubmitted) {
        formik.handleSubmit();
      }
    }
    return () => clearTimeout(timer);
  }, [countdown, formik, isCountdownActive, isFormSubmitted]);

  return (
    <div>
      {userClaims.admin ? (
        <div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleButtonClick}
            disabled={isCountdownActive || isFormSubmitted}
          >
            Create New Card Fee
          </Button>

          <Dialog
            open={showForm}
            onClose={handleCloseForm}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
              New Card Fee
            </DialogTitle>
            <DialogContent
              sx={{ backgroundColor: theme.palette.background.alt }}
            >
              {isCountdownActive ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      display: "inline-block",
                    }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={(countdown / 5) * 100}
                      color="secondary"
                    />
                    <Typography
                      variant="body1"
                      color="green"
                      style={{
                        position: "absolute",
                        top: "45%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 1,
                      }}
                    >
                      {countdown}
                    </Typography>
                  </div>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    style={{ marginTop: "8px" }}
                  >
                    Are you sure you give {formik.values.deliveryguyName}{" "}
                    {formik.values.price} birr for card fee?
                  </Typography>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleUndo}
                    style={{ marginTop: "16px" }}
                  >
                    Undo
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    startCountdown();
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Select Cards"
                        name="selectedCardIds"
                        variant="outlined"
                        multiple
                        value={formik.values.selectedCardIds}
                        onChange={handleCardsChange}
                        onBlur={formik.handleBlur}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            marginTop: "8px",
                            color: theme.palette.secondary[100],
                          },
                        }}
                        error={
                          formik.touched.selectedCardIds &&
                          Boolean(formik.errors.selectedCardIds)
                        }
                        helperText={
                          formik.touched.selectedCardIds &&
                          formik.errors.selectedCardIds
                        }
                        SelectProps={{
                          multiple: true,
                          MenuProps: {
                            PaperProps: {
                              style: {
                                backgroundColor: theme.palette.background.alt,
                                color: "inherit",
                                maxHeight: 300,
                              },
                            },
                          },
                          renderValue: (selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                alignItems: "center",
                              }}
                            >
                              {selected.length === 0 ? (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  No cards selected
                                </Typography>
                              ) : (
                                selected.map((id, index) => {
                                  const card = cardsOptions.find(
                                    (c) => c.id === id
                                  );
                                  return (
                                    <React.Fragment key={id}>
                                      <Chip
                                        label={`${
                                          card?.block || `Card (${id})`
                                        }`}
                                        sx={{
                                          backgroundColor:
                                            theme.palette.primary.main,
                                          color:
                                            theme.palette.primary.contrastText,
                                          fontWeight: 500,
                                          "&:hover": {
                                            backgroundColor:
                                              theme.palette.primary.dark,
                                          },
                                        }}
                                        size="small"
                                      />
                                      {index < selected.length - 1 && (
                                        <Box
                                          sx={{
                                            width: "1px",
                                            height: "20px",
                                            backgroundColor:
                                              theme.palette.divider,
                                            mx: 0.5,
                                          }}
                                        />
                                      )}
                                    </React.Fragment>
                                  );
                                })
                              )}
                            </Box>
                          ),
                        }}
                      >
                        {cardsOptions.length === 0 ? (
                          <MenuItem
                            disabled
                            sx={{
                              backgroundColor: theme.palette.background.alt,
                              color: theme.palette.secondary[100],
                              py: 1.5,
                              "&:not(:last-child)": {
                                borderBottom: `1px solid ${theme.palette.divider}`,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <Typography variant="body2">
                                No completed cards found
                              </Typography>
                            </Box>
                          </MenuItem>
                        ) : (
                          cardsOptions.map((option, index) => {
                            const isSelected =
                              formik.values.selectedCardIds.includes(option.id);

                            return (
                              <MenuItem
                                key={option.id}
                                value={option.id}
                                sx={{
                                  backgroundColor: isSelected
                                    ? theme.palette.primary.main
                                    : theme.palette.background.alt,
                                  color: isSelected
                                    ? theme.palette.primary.contrastText
                                    : theme.palette.secondary[100],
                                  py: 1.5,
                                  "&:not(:last-child)": {
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                  },
                                  "&:hover": {
                                    backgroundColor: isSelected
                                      ? theme.palette.primary.dark
                                      : theme.palette.primary.light,
                                    color: isSelected
                                      ? theme.palette.primary.contrastText
                                      : theme.palette.primary.contrastText,
                                  },
                                  "&.Mui-selected": {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.primary.dark,
                                    },
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={isSelected ? 600 : 400}
                                  >
                                    {option.block}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      ml: 2,
                                      color: isSelected
                                        ? theme.palette.primary.contrastText
                                        : theme.palette.secondary[300],
                                      fontWeight: 500,
                                    }}
                                  >
                                    {option.amount
                                      ? `${option.amount} Birr`
                                      : "N/A"}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            );
                          })
                        )}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={6}>
                      <TextField
                        select
                        name="deliveryguyId"
                        variant="outlined"
                        fullWidth
                        required
                        value={formik.values.deliveryguyId}
                        onChange={handleDeliveryGuyChange}
                        onBlur={formik.handleBlur}
                        label="Delivery Guy"
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            marginTop: "8px",
                            color: theme.palette.secondary[100],
                          },
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              style: {
                                backgroundColor: theme.palette.background.alt,
                                color: "inherit",
                              },
                            },
                          },
                        }}
                        error={
                          formik.touched.deliveryguyId &&
                          Boolean(formik.errors.deliveryguyId)
                        }
                        helperText={
                          formik.touched.deliveryguyId &&
                          formik.errors.deliveryguyId
                        }
                      >
                        <MenuItem value="">Select Delivery Guy</MenuItem>
                        {transformedData?.map((branch) => (
                          <MenuItem key={branch[1]} value={branch[1]}>
                            {branch[0]}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <CustomTextField
                      name="numberOfCard"
                      label="Number Of Card"
                      value={formik.values.numberOfCard}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      errors={formik.errors}
                      touched={formik.touched}
                      type="number"
                      InputProps={{
                        readOnly: true,
                      }}
                    />

                    <CustomTextField
                      name="price"
                      label="Price"
                      value={formik.values.price}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      errors={formik.errors}
                      touched={formik.touched}
                      type="number"
                      InputProps={{
                        readOnly: true,
                      }}
                    />

                    <Grid
                      item
                      xs={12}
                      container
                      alignItems="end"
                      justifyContent="end"
                    >
                      <DialogActions
                        sx={{ backgroundColor: theme.palette.background.alt }}
                      >
                        <Button
                          variant="contained"
                          onClick={handleCloseForm}
                          color="secondary"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={formik.isSubmitting}
                        >
                          {formik.isSubmitting ? (
                            <CircularProgress size={24} />
                          ) : (
                            "Submit"
                          )}
                        </Button>
                      </DialogActions>
                    </Grid>
                  </Grid>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
};

export default CardFeeReportForm;
